import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { RICHES, TEXTES } from './traductions-it.jsx';

/**
 * Bilingue français / italien.
 *
 * EASY2LOG est financé par Interreg Marittimo Italie-France et ses partenaires
 * sont italiens : ces deux écrans sont montrés en comité de pilotage. La langue
 * de travail reste le français — c'est celle de l'exploitant du port, qui s'en
 * sert pour comprendre un incident.
 *
 * LA CLÉ DE TRADUCTION EST LE TEXTE FRANÇAIS LUI-MÊME, pas un identifiant.
 * Trois raisons, et la troisième est la plus importante :
 *
 * 1. le JSX reste lisible — on y lit la phrase, pas `reglages.qr.lu` ;
 * 2. les modules purs (`reglages`, `timeline-labels`, `previsionnel`…) gardent
 *    leurs libellés français et leurs tests : la traduction se fait au bord, sur
 *    la valeur qu'ils rendent, et pas un seul test n'a eu à bouger ;
 * 3. une traduction manquante affiche le FRANÇAIS. Avec des identifiants, elle
 *    afficherait `reglages.qr.lu` — devant un comité de pilotage, une phrase
 *    dans la mauvaise langue se pardonne, un identifiant nu ne se pardonne pas.
 */

export type Langue = 'fr' | 'it';

const CLE_STOCKAGE = 'easy2log.langue';

/**
 * Le choix survit au rechargement, mais il est LOCAL au navigateur : rien n'en
 * sort, rien n'est envoyé au serveur. L'accès est gardé — en navigation privée
 * ou avec les données de site bloquées, le simple fait de lire `localStorage`
 * lève, et l'écran ne doit pas mourir pour un choix de langue.
 */
function langueInitiale(): Langue {
  try {
    return window.localStorage.getItem(CLE_STOCKAGE) === 'it' ? 'it' : 'fr';
  } catch {
    return 'fr';
  }
}

const Contexte = createContext<{ langue: Langue; choisir: (l: Langue) => void }>({
  langue: 'fr',
  choisir: () => undefined,
});

export function FournisseurLangue({ children }: { children: ReactNode }): ReactElement {
  const [langue, setLangue] = useState<Langue>(langueInitiale);

  /*
   * `lang` sur <html> suit la langue choisie, au chargement COMME au clic.
   * Le poser seulement au clic laissait la page rechargée en italien annoncée
   * `lang="fr"` : la synthèse vocale la lisait avec l'accent français, et la
   * coupure des mots suivait les règles françaises.
   */
  useEffect(() => {
    document.documentElement.lang = langue;
  }, [langue]);

  const choisir = useCallback((l: Langue): void => {
    setLangue(l);
    try {
      window.localStorage.setItem(CLE_STOCKAGE, l);
    } catch {
      // Sans persistance le choix vaut pour la session : acceptable.
    }
  }, []);

  const valeur = useMemo(() => ({ langue, choisir }), [langue, choisir]);
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useLangue(): { langue: Langue; choisir: (l: Langue) => void } {
  return useContext(Contexte);
}

/**
 * Traduit une chaîne. Rend le français tel quel s'il n'y a pas d'italien.
 *
 * Les valeurs variables passent par des marqueurs `{nom}` plutôt que par une
 * concaténation : l'italien n'ordonne pas toujours la phrase comme le français,
 * et un texte coupé en morceaux ne se traduit pas.
 */
export function useT(): (fr: string, valeurs?: Record<string, string | number>) => string {
  const { langue } = useLangue();
  return useCallback(
    (fr: string, valeurs?: Record<string, string | number>): string => {
      const texte = langue === 'fr' ? fr : (TEXTES[fr] ?? fr);
      if (valeurs === undefined) return texte;
      return texte.replace(/\{(\w+)\}/g, (marqueur, nom: string) =>
        nom in valeurs ? String(valeurs[nom]) : marqueur,
      );
    },
    [langue],
  );
}

/**
 * Message d'erreur.
 *
 * Le code HTTP entre parenthèses est détaché AVANT la recherche : sans ça
 * chaque code produirait une clé différente — « refusée (500) », « (502) »… —
 * et aucune ne serait jamais traduite. Ce que le serveur renvoie en clair reste
 * tel quel : aucun dictionnaire ne peut couvrir un texte qu'on ne connaît pas.
 */
export function useTErreur(): (message: string) => string {
  const t = useT();
  return useCallback(
    (message: string): string => {
      const decoupe = /^(.*?)( \(\d{3}\))$/.exec(message);
      return decoupe === null ? t(message) : `${t(decoupe[1] ?? '')}${decoupe[2] ?? ''}`;
    },
    [t],
  );
}

/**
 * Locale de formatage des nombres et des dates.
 *
 * Elle suit la langue : l'italien groupe les milliers au point là où le français
 * les sépare par une espace. Un tableau de chiffres formatés à la française dans
 * une page italienne se remarque tout de suite.
 */
export function useLocale(): 'fr-FR' | 'it-IT' {
  const { langue } = useLangue();
  return langue === 'fr' ? 'fr-FR' : 'it-IT';
}

/**
 * Bloc à balisage — ceux qui portent du gras, du `<code>` ou de l'italique.
 * Le français reste écrit sur place, dans le JSX ; l'italien vit dans le
 * dictionnaire, sous la même clé, pour qu'un relecteur italophone n'ait qu'un
 * seul fichier à ouvrir.
 */
export function Riche({ cle, children }: { cle: string; children: ReactNode }): ReactElement {
  const { langue } = useLangue();
  if (langue === 'fr') return <>{children}</>;
  return <>{RICHES[cle] ?? children}</>;
}

/** Bascule de langue. Deux langues : un interrupteur, pas une liste déroulante. */
export function BasculeLangue(): ReactElement {
  const { langue, choisir } = useLangue();
  return (
    <span className="langues">
      {(['fr', 'it'] as const).map((l) => (
        <button
          key={l}
          type="button"
          className={l === langue ? 'active' : ''}
          aria-pressed={l === langue}
          lang={l}
          onClick={(): void => {
            choisir(l);
          }}
        >
          {l === 'fr' ? 'FR' : 'IT'}
        </button>
      ))}
    </span>
  );
}
